import { useEffect, useState } from "react";

import { useScrollLock } from "../../hooks/useScrollLock";

import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  ModalText,
  ModelNameInput,
  InputLabel,
  Row,
} from "./TrainModal.styles";

import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import Button from "../utils/Button/Button";
import { useNavigate } from "react-router-dom";

const TrainModal = ({ isOpen, onClose, onContinue, loading = false }) => {
  const [modelName, setModelName] = useState("");
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const navigate = useNavigate();

  useScrollLock(shouldRender);

  useEffect(() => {
    if (!isOpen) {
      setModelName("");
    }
  }, [isOpen]);

  useEffect(() => {
    const randomId = Math.floor(1000 + Math.random() * 9000);
    setModelName(`New Template #${randomId}`);
  }, [shouldRender]);

  // Kontrola renderovanja i animacije
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsAnimating(false); // Početno stanje bez animacije
      // Kratka pauza da se modal renderuje pre animacije
      const timer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      return () => cancelAnimationFrame(timer);
    } else if (shouldRender) {
      setIsAnimating(false);
      // Čeka da se animacija završi pre uklanjanja iz DOM-a
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // 300ms koliko traje animacija

      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  if (!shouldRender) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTutorialClick = () => {
    navigate("/train-tutorial");
    onClose();
  };

  const handleContinue = () => {
    if (modelName.trim() && !loading) {
      onContinue(modelName.trim(), "video");
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick} $isAnimating={isAnimating}>
      <ModalContainer $isAnimating={isAnimating}>
        <ModalContent>
          <Row>
            <ModalTitle>Train a new model</ModalTitle>
            <CloseButton onClick={onClose}>
              <CloseSvg />
            </CloseButton>
          </Row>
          <ModalText>
            Upload similar video clips showing the exact action you want to
            capture, and we'll train a custom template based on them. Once
            trained, your template lets you apply that same action to any other
            image - bringing the motion to life in new videos.
          </ModalText>

          <ModalText>
            This process creates a private action template that captures motion
            (not people) from your clips and applies the motion to any image you
            upload.
          </ModalText>

          <ModalText $bold>Share Your Template (Public Library)</ModalText>
          <ModalText>
            Submit for review only after training succeeds. Approved templates
            may go public. Staff can remove low - quality or duplicates anytime.
          </ModalText>

          <ModalText $bold>Creator Rewards</ModalText>

          <ModalText>
            • Earn 2 credits per use (first 60 days)
            <br />• Then 1 credit per use (after 60 days)
          </ModalText>

          <ModalText>
            Earned credits can be withdrawn in USDT or used on the platform.
          </ModalText>

          <ModalText $italic>
            VIP Creators receive higher earnings. Once you have 10+ high-quality
            templates live, you will be eligible to apply to join VIP.
          </ModalText>

          <ModalText onClick={handleTutorialClick} $click>
            📚 View our training tutorial to get started
          </ModalText>

          <InputLabel>Name</InputLabel>
          <ModelNameInput
            type="text"
            placeholder="Enter model name..."
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            maxLength={50}
          />

          <Button
            onClick={handleContinue}
            disabled={!modelName.trim() || loading}
            loading={loading}
            title={"Continue"}
            style={{ width: 180, alignSelf: "center" }}
          />
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TrainModal;
